import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  CAREERS_EMAIL,
  getAllJobs,
  getJob,
  getOpenJobs,
} from "@/data/jobsData";
import "@/pages/CareerRole.css";

export default function CareerRole() {
  const { slug } = useParams();
  const job = getJob(slug);
  const allJobs = getAllJobs();

  const heroApplyRef = useRef(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    if (!job) return;
    document.title = `${job.title} — Careers — Ōraṇi`;
    window.scrollTo(0, 0);
  }, [job]);

  // Show sticky bar only AFTER the hero Apply CTA has scrolled out of
  // view (so it never competes with the primary CTA in view).
  useEffect(() => {
    if (!job || job.status !== "open") return;
    const el = heroApplyRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setStickyVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [job]);

  if (!job) return <Navigate to="/careers" replace />;

  /* ------- CLOSED state ------- */
  if (job.status !== "open") {
    return (
      <main className="role" data-testid={`career-role-${job.slug}-closed`}>
        <section className="role__closed" data-testid="role-closed">
          <span className="role__eyebrow">Closed</span>
          <h1 className="role__closed-title">This role is currently closed.</h1>
          <p className="role__closed-body">
            We are always open to meeting thoughtful designers and
            makers. You can view open roles or write to us with your
            portfolio.
          </p>
          <div className="role__closed-ctas">
            <Link
              to="/careers"
              className="role__closed-cta"
              data-testid="role-closed-view-open"
            >
              View open roles
              <span aria-hidden="true" className="role__rule" />
            </Link>
            <a
              href={`mailto:${CAREERS_EMAIL}?subject=Portfolio%20%E2%80%94%20${encodeURIComponent(
                job.title
              )}`}
              className="role__closed-link"
              data-testid="role-closed-write"
            >
              Write to us
              <span aria-hidden="true" className="role__rule" />
            </a>
          </div>
        </section>
      </main>
    );
  }

  const otherOpen = getOpenJobs().filter((j) => j.slug !== job.slug);

  return (
    <main
      className="role"
      data-testid={`career-role-${job.slug}`}
      aria-label={`${job.title} — careers`}
    >
      {/* HERO */}
      <header className="role__hero" data-testid="role-hero">
        <h1 className="role__title" data-testid="role-title">
          {job.title}
        </h1>
        <p className="role__meta" data-testid="role-meta">
          {job.metaOverride ? (
            job.metaOverride
          ) : (
            <>
              {job.location}
              <span aria-hidden="true"> · </span>
              {job.type}
              {job.experience ? (
                <>
                  <span aria-hidden="true"> · </span>
                  {job.experience}
                </>
              ) : null}
            </>
          )}
        </p>

        <div className="role__hero-body">
          {job.heroIntro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="role__hero-ctas" ref={heroApplyRef}>
          <a
            href={job.applyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="role__apply"
            data-testid="role-apply-hero"
          >
            Apply now
            <span aria-hidden="true" className="role__rule" />
          </a>
        </div>
      </header>

      {/* BODY SECTIONS */}
      <article className="role__body" data-testid="role-body">
        {job.sections.map((s, idx) => (
          <section
            className="role__section"
            key={idx}
            data-testid={`role-section-${idx}`}
          >
            <h2 className="role__section-title">{s.title}</h2>
            {s.intro ? <p className="role__section-intro">{s.intro}</p> : null}
            {s.paragraphs
              ? s.paragraphs.map((p, i) => (
                  <p className="role__section-p" key={i}>
                    {p}
                  </p>
                ))
              : null}
            {s.subheading ? (
              <p className="role__section-subheading">{s.subheading}</p>
            ) : null}
            {s.bullets ? (
              <ul className="role__bullets">
                {s.bullets.map((b, i) => (
                  <li key={i}>
                    <span className="role__bullet-mark" aria-hidden="true" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </article>

      {/* QUESTIONS + FINAL APPLY */}
      <section className="role__end" data-testid="role-end">
        <span className="role__end-rule" aria-hidden="true" />
        <h2 className="role__end-title">Have questions?</h2>
        <p className="role__end-body">
          Write to us at{" "}
          <a
            href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(
              job.title
            )}%20%E2%80%94%20Question`}
            className="role__end-mail"
            data-testid="role-end-mail"
          >
            {CAREERS_EMAIL}
          </a>{" "}
          with the role name in the subject line.
        </p>
      </section>

      {/* OTHER OPEN ROLES */}
      {otherOpen.length > 0 && (
        <section
          className="role__other"
          data-testid="role-other"
          aria-label="Other open roles"
        >
          <span className="role__eyebrow">Other open roles</span>
          <ul className="role__other-list">
            {otherOpen.map((j) => (
              <li key={j.slug}>
                <Link
                  to={`/careers/${j.slug}`}
                  className="role__other-link"
                  data-testid={`role-other-${j.slug}`}
                >
                  <h3 className="role__other-title">{j.title}</h3>
                  <p className="role__other-meta">
                    {j.metaOverride ? (
                      j.metaOverride
                    ) : (
                      <>
                        {j.location}
                        <span aria-hidden="true"> · </span>
                        {j.type}
                      </>
                    )}
                  </p>
                  <span className="role__other-cta" aria-hidden="true">
                    View role
                    <span className="role__rule" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* STICKY APPLY BAR */}
      <div
        className={`role__sticky${stickyVisible ? " is-visible" : ""}`}
        data-testid="role-sticky"
        aria-hidden={!stickyVisible}
      >
        <div className="role__sticky-inner">
          <div className="role__sticky-left">
            <span className="role__sticky-title">{job.title}</span>
            <span className="role__sticky-meta">
              {job.location}
              <span aria-hidden="true"> · </span>
              {job.type}
            </span>
          </div>
          <div className="role__sticky-right">
            <a
              href={job.applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="role__sticky-apply"
              data-testid="role-sticky-apply"
            >
              Apply now
              <span aria-hidden="true" className="role__rule" />
            </a>
          </div>
        </div>
      </div>

      {/* unused fallback to keep eslint happy on future expansion */}
      {!allJobs ? null : null}
    </main>
  );
}
